import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import InteractiveTechBackground from './InteractiveTechBackground';
import SpotlightCard from './SpotlightCard';

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 50, damping: 20 }
    }
  };

  const stats = [
    { number: "4.800+", label: "Colaboradores" },
    { number: "25", label: "Anos de Experiência" },
    { number: "1.900+", label: "Localidades Atendidas" },
    { number: "148.000", label: "Ativos de TI Atendidos" }
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-24 pb-12 overflow-hidden bg-background">
      <InteractiveTechBackground />

      {/* Overlay to ensure text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/10 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="grid lg:grid-cols-2 gap-12 items-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary mb-8 backdrop-blur-sm">
              <span className="relative flex h-3 w-3" aria-hidden="true">
                <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
              <span className="text-sm font-semibold tracking-wide uppercase">25 Anos de Excelência em TI</span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl lg:text-7xl font-bold font-heading leading-tight mb-6 tracking-tight text-white">
              Obtenha Soluções <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-glow to-primary">
                Profissionais de TI para sua Empresa
              </span>
            </motion.h1>

            <motion.p variants={itemVariants} className="text-xl text-gray-400 mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Transforme sua empresa com soluções de TI estratégicas. Atendemos mais de
              <strong className="text-white"> 1.900 localidades</strong> em todo o Brasil com segurança, agilidade e performance.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href="https://api.whatsapp.com/send?phone=5511964158980&text=Ol%C3%A1%2C%20gostaria%20de%20falar%20com%20um%20especialista."
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white transition-all duration-200 bg-primary rounded-full hover:bg-[#b91f2f] hover:shadow-lg hover:shadow-primary/30 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Fale com um Especialista <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 h-full w-full scale-0 rounded-full transition-all duration-300 group-hover:scale-100 group-hover:bg-primary-glow/30" />
              </a>

              <Link to="/resultados" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white transition-all duration-200 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-white/20 backdrop-blur-md">
                Ver Nossos Resultados
              </Link>
            </motion.div>
          </div>

          {/* Right Stats Grid */}
          <motion.div className="grid grid-cols-2 gap-3 md:gap-6" variants={containerVariants}>
            {stats.map((stat, index) => (
              <motion.div key={index} variants={itemVariants} whileHover={{ scale: 1.05 }} className="h-full">
                <SpotlightCard className="h-full bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 backdrop-blur-sm hover:border-primary/50 transition-colors" spotlightColor="rgba(220, 38, 38, 0.2)">
                  <div className="flex flex-col items-center text-center h-full justify-center">
                    <h3 className="text-2xl md:text-4xl font-extrabold text-white mb-1 md:mb-2">{stat.number}</h3>
                    <p className="text-xs md:text-sm font-medium text-gray-400 uppercase tracking-wider">{stat.label}</p>
                  </div>
                </SpotlightCard>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
